/**
 * Push 발송 API (어드민)
 *
 * 백엔드 API 명세서(BACKEND_API_SPEC.md §4) 기반:
 * - GET    /api/admin/push-messages
 * - GET    /api/admin/push-messages/{id}
 * - POST   /api/admin/push-messages
 * - POST   /api/admin/push-messages/{id}/cancel
 * - DELETE /api/admin/push-messages/{id}
 */
import { apiClient } from './client';

export type PushTargetApp = 'BUSINESS' | 'GUARDIAN';

export type PushSendMode = 'NOW' | 'SCHEDULED';

export type PushSendStatus = 'SCHEDULED' | 'SENT' | 'CANCELED';

export interface PushMessage {
  id: number;
  targetApp: PushTargetApp;
  title: string;
  body: string;
  link?: string | null;
  scheduledAt?: string | null;
  sentAt?: string | null;
  sendStatus: PushSendStatus;
  sentCount: number;
  createdAt: string;
}

export interface PushMessageCreateInput {
  targetApp: PushTargetApp;
  title: string;
  body: string;
  link?: string | null;
  sendMode: PushSendMode;
  scheduledAt?: string | null;
}

export interface GetPushMessagesParams {
  page?: number;
  size?: number;
  targetApp?: PushTargetApp | 'ALL';
  status?: PushSendStatus | 'ALL';
  search?: string;
}

export interface PushMessagePage {
  content: PushMessage[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface CreatePushMessageResult {
  message: PushMessage;
  sentCount: number;
}

const BASE = '/admin/push-messages';

function cleanFilter<T extends string>(v: T | 'ALL' | undefined): T | undefined {
  return v && v !== 'ALL' ? (v as T) : undefined;
}

export const pushMessagesApi = {
  getPushMessages: async (
    params: GetPushMessagesParams = {},
  ): Promise<PushMessagePage> => {
    const res = await apiClient.get<PushMessagePage>(BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 10,
        targetApp: cleanFilter(params.targetApp),
        status: cleanFilter(params.status),
        search: params.search?.trim() || undefined,
      },
    });
    return res.data;
  },

  getPushMessage: async (id: number): Promise<PushMessage> => {
    const res = await apiClient.get<PushMessage>(`${BASE}/${id}`);
    return res.data;
  },

  /**
   * Push 발송 요청.
   *
   * 백엔드 정책: 실제 FCM 발송에 시간이 걸려 응답이 늦으므로
   * 클라이언트는 **fire-and-forget** 으로 요청만 보내고 즉시 성공 처리한다.
   * - 요청은 백그라운드로 전송되고 응답/에러는 무시
   * - 실제 발송 결과는 목록 페이지에서 갱신된 데이터로 확인
   */
  createPushMessage: async (
    input: PushMessageCreateInput,
  ): Promise<CreatePushMessageResult> => {
    // 응답을 기다리지 않고 즉시 반환. 실패해도 무시.
    void apiClient.post<CreatePushMessageResult>(BASE, input).catch(() => {
      // 백엔드가 long-running으로 timeout 나는 게 정상. 무시.
    });

    // 클라이언트 표시용 mock 응답 (실제 sentCount는 추후 목록 새로고침 시 반영)
    const now = (() => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      );
    })();
    const isScheduled = input.sendMode === 'SCHEDULED';
    return {
      message: {
        id: 0,
        targetApp: input.targetApp,
        title: input.title,
        body: input.body,
        link: input.link ?? null,
        scheduledAt: isScheduled ? (input.scheduledAt ?? null) : null,
        sentAt: isScheduled ? null : now,
        sendStatus: isScheduled ? 'SCHEDULED' : 'SENT',
        sentCount: 0,
        createdAt: now,
      },
      sentCount: 0,
    };
  },

  cancelPushMessage: async (id: number): Promise<PushMessage> => {
    const res = await apiClient.post<PushMessage>(`${BASE}/${id}/cancel`);
    return res.data;
  },

  deletePushMessage: async (id: number): Promise<void> => {
    await apiClient.delete<void>(`${BASE}/${id}`);
  },
};
